
import { Component, Vue } from "vue-property-decorator";
import { DBInfo } from "@/survey";

@Component({
  components: {
    InfoItem: {
      functional: true,
      props: {
        label: String,
        value: [String, Number],
        detail: String,
        status: String,
        wide: Boolean
      },
      render(createElement: any, context: any) {
        const status = context.props.status || "neutral";
        const children = [
          createElement("span", context.props.label),
          createElement("strong", { class: [`security-value-${status}`] }, context.props.value || "Unknown")
        ];
        if (context.props.detail) {
          children.push(createElement("small", context.props.detail));
        }
        return createElement("div", { class: { "info-item": true, "info-item-wide": context.props.wide } }, children);
      }
    },
    UtilizationBar: {
      functional: true,
      props: {
        label: String,
        value: Number,
        detail: String
      },
      render(createElement: any, context: any) {
        const value = Math.max(0, Math.min(100, Number(context.props.value) || 0));
        return createElement("div", { class: "utilization" }, [
          createElement("div", { class: "utilization-top" }, [
            createElement("span", context.props.label),
            createElement("strong", `${value.toFixed(1)}%`)
          ]),
          createElement("div", { class: "utilization-track" }, [
            createElement("div", {
              class: {
                "utilization-fill": true,
                warning: value >= 75 && value < 90,
                danger: value >= 90
              },
              style: { width: `${value}%` }
            })
          ]),
          createElement("small", context.props.detail || "No detail available")
        ]);
      }
    }
  }
})
export default class DBInfoPage extends Vue {
  client = Vue.prototype.$surveyClient;
  dbinfo: DBInfo | null = null;
  loading = false;
  error = "";
  filter = "";

  get fields() {
    return [
      { key: "t1", sortable: true, label: "Table Name" },
      { key: "t2", sortable: true, label: "Schema" },
      { key: "t3", sortable: true, label: "Size", formatter: (value: number) => this.formatBytes(value * 1024 * 1024) },
      { key: "t4", sortable: true, label: "Rows", formatter: (value: number) => value.toLocaleString() }
    ];
  }

  async created() {
    await this.loadDatabaseInfo();
  }

  async loadDatabaseInfo() {
    this.loading = true;
    this.error = "";
    try {
      this.dbinfo = await this.client.getDbInfo();
    } catch (e) {
      this.dbinfo = null;
      this.error = `Unable to load database information: ${e}`;
    } finally {
      this.loading = false;
    }
  }

  get tableCount() {
    return this.dbinfo === null ? 0 : this.dbinfo.table_info.length;
  }

  get totalRows() {
    if (this.dbinfo === null) {
      return 0;
    }
    return this.dbinfo.table_info.reduce((sum, item) => sum + item.t4, 0);
  }

  get totalSizeMB() {
    if (this.dbinfo === null) {
      return 0;
    }
    return this.dbinfo.table_info.reduce((sum, item) => sum + item.t3, 0);
  }

  get databaseSizeBytes() {
    return this.totalSizeMB * 1024 * 1024;
  }

  get diskInfo() {
    return this.dbinfo?.disk_info || [];
  }

  get databaseSecurityInfo() {
    return this.dbinfo?.database_security_info || [];
  }

  get physicalMemoryPercent() {
    if (this.dbinfo === null || this.dbinfo.total_physical_memory <= 0) {
      return 0;
    }
    return this.dbinfo.used_physical_memory / this.dbinfo.total_physical_memory * 100;
  }

  get physicalMemoryDetail() {
    if (this.dbinfo === null) {
      return "No memory metrics reported";
    }
    const available = this.dbinfo.available_physical_memory || this.dbinfo.free_physical_memory || 0;
    return `${this.formatBytes(this.dbinfo.used_physical_memory)} used of ${this.formatBytes(this.dbinfo.total_physical_memory)} (${this.formatBytes(available)} available)`;
  }

  get jvmHeapPercent() {
    if (this.dbinfo === null || this.dbinfo.jvm_heap_max <= 0) {
      return 0;
    }
    return this.dbinfo.jvm_heap_used / this.dbinfo.jvm_heap_max * 100;
  }

  percentValue(value: number) {
    return Math.max(0, Math.min(100, (value || 0) * 100));
  }

  diskPercent(disk: { total: number, used: number }) {
    return disk.total > 0 ? disk.used / disk.total * 100 : 0;
  }

  cpuDetail(value: number) {
    if (!value || value <= 0) {
      return "Metric not reported by this operating system/JVM";
    }
    return `${this.percentValue(value).toFixed(1)}% current load`;
  }

  formatLoad(value: number) {
    return value && value > 0 ? value.toFixed(2) : "Not reported";
  }

  formatJvmMax(value: number) {
    return value && value > 0 ? this.formatBytes(value) : "unbounded heap";
  }

  formatDuration(milliseconds: number) {
    if (!milliseconds || milliseconds < 0) {
      return "Unknown";
    }
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  formatBytes(value: number) {
    if (!value || value <= 0) {
      return "0 B";
    }
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size = size / 1024;
      unitIndex += 1;
    }
    const digits = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(digits)} ${units[unitIndex]}`;
  }
}
